import React, {useEffect, useState} from 'react';
import SelectComponent from "../../../components/select-component";
import TemplateDefault from "../../../components/template-default";
import Button from "../../../components/button";
import InputForm from "../../../components/input-form";
import Card from "../../../components/card";
import resalesService from "../../../services/resales-service";
import {useQuery} from "@tanstack/react-query";
import {toast} from "react-toastify";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {AlertMessage} from "../../../utils/alert-message";
import {MaskDefault} from "../../../utils/mask";
import {useNavigate, useParams} from "react-router-dom";
import InputText from "../../../components/input-text";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import ContactsCellRenderer from "../../dashboard/components/contacts-cell-renderer";
import ContactsModal from "../../dashboard/components/contacts-modal";
import ProjectRender from "../components/projectRender";
import general from "../../../utils/general";

const MySwal = withReactContent(Swal);

const initialState = {
    name: "",
    phone: "",
    cnpj: "",
    email: "",
    contract_start: "",
    contract_end: "",
    contact: "",
    type_pcl: [],
    related_projects: []
}

const pclOptions = [
    {value: "SMARTCAPTURE", label: "Smartcapture"},
    {value: "API", label: "API"},
    {value: "SENTRYCLOUD", label: "SentryCloud"}
]

const ResalesForm = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    useQuery({
        queryFn: () => resalesService.getById(id).then((data) => {
            data.type_pcl = JSON.parse(data.type_pcl)?.map(t => pclOptions.find(p => p.value === t)) ?? [];

            Object.keys(data).forEach(key => data[key] = data[key] ?? "");

            setState(data);

            return data;
        }),
        queryKey: ['resale', {id}],
        enabled: !!id,
    });

    const submit = () => {
        setIsLoading(true);

        const formData = new FormData();
        Object.keys(state).forEach(key => {
            if (key === "type_pcl") {
                formData.append("type_pcl", state.type_pcl.map(t => t.value));
                return;
            }

            formData.append(key, state[key]);
        });

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? resalesService.edit(formData, id) : resalesService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                navigate(`/resales/form/${data.id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }

    const deleteResale = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir a revenda?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                resalesService.delete(id)
                    .then(() => {
                        navigate("/resales");
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const handleInput = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value}));
    const handleCnpj = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value.toUpperCase()}));
    const handleInputMask = ({target: {value, id}}) => {
        const clearValue = value.replace(/[.,\s]/g, "");
        setState(prev => ({...prev, [id]: clearValue}));
    }
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState([{
        principal: "",
        data: "",
        title: ""
    }]);
    const contactsRender = (data, value) => {
        setModalData({
            principal: value,
            data: data.contacts,
            title: data.name,
        })

        setOpenModal(true)
    }

    const [columnDefs] = useState([
        {headerName: "Projetos relacionados", field: "name", cellRenderer: ProjectRender, flex: 1, sortable: true, resizable: false },
        {field: "contact_central", headerName: "Contatos", cellRenderer: ContactsCellRenderer,
            cellRendererParams: {
                contactsRender: contactsRender,
            },
            width: 75},
        {headerName: "Data de expiração", field: "expiration_date", flex: 1, sortable: true, resizable: false,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return general.nullLastDateStringComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
    ]);

    const HEADER_HEIGHT = 42;
    const ROW_HEIGHT = 32;
    const MAX_HEIGHT = 300;

    const gridHeight = Math.min(
        HEADER_HEIGHT + state.related_projects.length * ROW_HEIGHT,
        MAX_HEIGHT
    );

    const observationHeight = state.related_projects.length > 0
        ? `calc(100vh - ${546 + gridHeight}px)`
        : "calc(100vh - 537px)";

    return (
        <TemplateDefault title={"Cadastro de revenda" + (id ? ` #${id}` : "")} isLoading={isLoading}>
            <Card title="Dados">
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">

                    <InputForm id="name" name="name" value={state.name} onChange={handleInput} label="Nome *" col="col-span-5"/>

                    <InputForm id="phone" name="phone" value={state.phone} onBlur={handleInputMask} mask={MaskDefault.phone}
                               label="Telefone" col="col-span-3" whatsApp={true}/>

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            instanceId="type_pcl"
                            label="Tipos de PCL"
                            options={pclOptions}
                            value={state.type_pcl}
                            onChange={(value) => setState(prev => ({...prev, type_pcl: value}))}
                            isMulti
                        />
                    </div>

                    <div className={"col-span-2 cnpj-class"}>
                        <InputForm id="cnpj" name="cnpj" value={state.cnpj} onChange={handleCnpj} mask={MaskDefault.cnpj} label="CNPJ"/>
                    </div>

                    <InputForm id="email" name="email" value={state.email} onChange={handleInput} label="E-Mail" col="col-span-4"
                               type="email"/>

                    <InputForm id="contract_start" name="contract_start" value={state.contract_start} onChange={handleInput}
                               label="Início do contrato" col="col-span-2" type="date"/>

                    <InputForm id="contract_end" name="contract_end" value={state.contract_end} onChange={handleInput}
                               label="Fim do contrato" col="col-span-2" type="date"/>

                    <InputForm id="contact" name="contact" value={state.contact} onChange={handleInput} label="Contato"
                               col="col-span-2"/>

                    <InputForm id="commercial_contact"
                               name="commercial_contact" value={state.commercial_contact}
                               onChange={handleInput} label="Contato comercial"
                               col="col-span-4"/>
                    <InputForm
                        col="col-span-2"
                        id={"zipcode"}
                        label={"CEP"}
                        onChange={handleInput}
                        value={state?.zipcode}
                        mask={MaskDefault.cep}
                    />
                    <InputForm
                        col="col-span-4"
                        id={"street"}
                        label={"Logradouro"}
                        onChange={handleInput}
                        value={state?.street}
                    />
                    <InputForm
                        col="col-span-2"
                        id={"number"}
                        label={"Número"}
                        onChange={handleInput}
                        value={state?.number}
                    />

                    <InputForm
                        col="col-span-4"
                        id={"neighborhood"}
                        label={"Bairro"}
                        onChange={handleInput}
                        value={state?.neighborhood}
                    />
                    <InputForm
                        col="col-span-4"
                        id={"city"}
                        label={"Cidade/UF"}
                        onChange={handleInput}
                        value={state?.city}
                    />
                    <InputForm
                        col="col-span-4"
                        id={"complement"}
                        label={"Complemento"}
                        onChange={handleInput}
                        value={state?.complement}
                    />

                    <InputForm id="legal_representative"
                               name="legal_representative" value={state.legal_representative}
                               onChange={handleInput} label="Representante legal - Nome"
                               col="col-span-7"/>
                    <InputForm id="legal_representative_cpf"
                               name="legal_representative_cpf" value={state.legal_representative_cpf}
                               onChange={handleInput} label="Representante legal - CPF"
                               mask={MaskDefault.cpf}
                               col="col-span-5"/>
                    <InputForm id="witness"
                               name="witness" value={state.witness}
                               onChange={handleInput} label="Testemunha  - Nome"
                               col="col-span-7"/>
                    <InputForm id="witness_cpf"
                               name="witness_cpf" value={state.witness_cpf}
                               onChange={handleInput} label="Testemunha  - CPF"
                               mask={MaskDefault.cpf}
                               col="col-span-5"/>

                    <InputText id="observation"
                               name="observation" value={state.observation}
                               onChange={handleInput} label="Observações"
                               style={{
                                   height: observationHeight,
                                   minHeight: "150px",
                               }}
                               col="col-span-12"/>


                    {state.related_projects.length > 0 &&
                        <div className="ag-theme-quartz grid col-span-12" style={{height: gridHeight}}>
                            <AgGridReact
                                rowHeight={32}
                                rowData={state.related_projects}
                                columnDefs={columnDefs}
                                gridOptions={{localeText: LocalizationAgGrid}}
                            />
                        </div>
                    }

                </div>
            </Card>

            <Card wrapperClass="mt-5">
                <div className="flex justify-end gap-3">
                    {
                        id &&
                        <Button onClick={deleteResale} target="danger">
                            Excluir
                        </Button>
                    }
                    <Button onClick={() => navigate("/resales")} target="warning">
                        Cancelar
                    </Button>
                    <Button onClick={submit} target="success">
                        Salvar
                    </Button>
                </div>
            </Card>

            <ContactsModal open={openModal} set={setOpenModal} modalData={modalData}/>
        </TemplateDefault>
    );
}

export default ResalesForm;
